import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, CheckCircle2, ArrowRight, Zap, FileText, Target,
  ExternalLink, Search, Lock, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const textPrimary = "text-slate-900";
  const textSecondary = "text-slate-600";
  const textMuted = "text-slate-500";
  const borderCls = "border-slate-200";
  const cardCls = "bg-white border-slate-200 shadow-sm";

  const freeFeatures = [
    "Visibility score across 47 data points",
    "Prioritized issues hurting your rankings",
    "Top 3 competitors comparison",
    "Map Pack visibility analysis",
    "AI search readiness check",
  ];

  const paidFeatures = [
    "Personalized fix steps for every issue",
    "Copy-paste GBP description & review templates",
    "Full competitor gap analysis",
    "90-day week-by-week action plan",
    "Website SEO analysis",
    "Downloadable PDF fix sheet",
  ];

  return (
    <div className={`min-h-screen ${"bg-slate-50"}`}>
      <header className={`border-b backdrop-blur-sm sticky top-0 z-50 ${"bg-white/80 border-slate-200"}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="pricing-logo">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className={`font-bold text-xl ${textPrimary}`}>Rankify<span className="text-blue-500">Local</span></span>
            </button>
            <div className="flex items-center gap-3">
              {user ? (
                <Button onClick={() => navigate("/dashboard")} variant="outline" size="sm" className={"border-slate-300 text-slate-700"}>Dashboard</Button>
              ) : (
                <Button onClick={() => navigate("/login")} variant="outline" size="sm" className={"border-slate-300 text-slate-700"}>Sign In</Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 mb-4">Simple Pricing</Badge>
          <h1 className={`text-4xl sm:text-5xl font-bold mb-4 ${textPrimary}`}>Free Audit. $27 Fix Plan.</h1>
          <p className={`text-lg max-w-2xl mx-auto ${textSecondary}`}>
            Every audit starts free. If you want the full action plan with personalized fixes, unlock it for a one-time $27 payment per report.
          </p>
        </motion.div>

        {/* Two-column comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {/* Free Report */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className={`h-full ${cardCls}`} data-testid="free-plan-card">
              <CardContent className="p-6 md:p-8">
                <div className={`w-12 h-12 rounded-xl bg-slate-500/10 flex items-center justify-center mb-4`}>
                  <Search className="w-6 h-6 text-slate-500" />
                </div>
                <h2 className={`text-2xl font-bold mb-1 ${textPrimary}`}>Free Audit</h2>
                <p className={`text-sm mb-5 ${textSecondary}`}>See what's hurting your local visibility</p>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${textPrimary}`}>$0</span>
                  <span className={`text-sm ml-1 ${textMuted}`}>always free</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {freeFeatures.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${textSecondary}`}>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate("/")} className="w-full bg-slate-600 hover:bg-slate-700 text-white font-semibold" data-testid="free-plan-cta">
                  Run Free Audit <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* $27 Report Unlock */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className={`h-full border-2 border-blue-500 ${"bg-blue-50/50"}`} data-testid="paid-plan-card">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center`}>
                    <Sparkles className="w-6 h-6 text-blue-500" />
                  </div>
                  <Badge className="bg-blue-500 text-white border-0">Best Value</Badge>
                </div>
                <h2 className={`text-2xl font-bold mb-1 ${textPrimary}`}>Full Report Unlock</h2>
                <p className={`text-sm mb-5 ${textSecondary}`}>Get your personalized fix plan</p>
                <div className="mb-2">
                  <span className={`text-4xl font-bold ${textPrimary}`}>$27</span>
                  <span className={`text-sm ml-1 ${textMuted}`}>per report, one-time</span>
                </div>
                <p className={`text-xs mb-5 ${textMuted}`}>SEO agencies charge $300-500 for this analysis</p>
                <ul className="space-y-3 mb-8">
                  {paidFeatures.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${textSecondary}`}>
                      <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate("/")} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="paid-plan-cta">
                  Run Audit & Unlock <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className={`text-xs text-center mt-3 ${textMuted}`}>One-time payment. Not a subscription. Keep it forever.</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* How it works */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
          <h2 className={`text-2xl font-bold text-center mb-8 ${textPrimary}`}>How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Search, title: "1. Run Free Audit", desc: "Enter your business name. We scan your Google profile and pull competitor data in under 60 seconds." },
              { icon: FileText, title: "2. See Your Score", desc: "Get your visibility score, issues found, and how you compare to local competitors — completely free." },
              { icon: Lock, title: "3. Unlock Full Plan", desc: "Pay $27 to unlock personalized fix steps, copy-paste templates, and your 90-day action plan." },
            ].map((step, i) => (
              <Card key={i} className={cardCls}>
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${"bg-blue-50"}`}>
                    <step.icon className="w-6 h-6 text-blue-500" />
                  </div>
                  <h3 className={`font-semibold mb-2 ${textPrimary}`}>{step.title}</h3>
                  <p className={`text-sm ${textSecondary}`}>{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Done-for-you services link */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Card className="bg-gradient-to-br from-blue-600 to-blue-800 border-0">
            <CardContent className="p-8 md:p-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Want Us to Do It For You?</h2>
              <p className="text-blue-100 mb-6 max-w-xl mx-auto">
                Don't have time to implement the fixes yourself? Our team offers done-for-you local SEO services — from GBP optimization to review management and beyond.
              </p>
              <a href="https://rankifylocal.com/pricing" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-semibold" data-testid="done-for-you-cta">
                  View Our Services <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
